/**
 * Shoplift price-test aware price writer.
 *
 * Shoplift's script rewrites elements tagged with data-sl-attribute-* after
 * page load to display the test price. Theme scripts that later write into
 * those elements (variant switching, customizer drawers) would clobber the
 * test price back to the Shopify price, making the test price flash and
 * revert. This helper measures the adjustment Shoplift applied — the
 * difference between the price shown in the element and the unadjusted price
 * the theme last rendered there — and carries it over on every write.
 *
 * With no active price test the adjustment is 0 and write() behaves exactly
 * like `el.textContent = format(cents)`.
 */
window.ShopliftPrice = (() => {
  /* Unadjusted cents the theme last wrote, per element. */
  const lastWritten = new WeakMap();

  const parseCents = (text) => {
    const str = String(text).trim();
    if (!str) return null;
    
    const match = str.match(/[\d.,]+/);
    if (!match) return null;
    let numStr = match[0];
    
    const lastComma = numStr.lastIndexOf(',');
    const lastDot = numStr.lastIndexOf('.');
    const decimalPos = Math.max(lastComma, lastDot);
    
    if (decimalPos !== -1) {
      const charsAfter = numStr.length - 1 - decimalPos;
      // If the last separator is followed by 1 or 2 digits, it's a decimal separator
      if (charsAfter === 1 || charsAfter === 2) {
        if (decimalPos === lastComma) {
          numStr = numStr.replace(/\./g, '').replace(',', '.');
        } else {
          numStr = numStr.replace(/,/g, '');
        }
      } else {
        // Otherwise it's a thousands separator
        numStr = numStr.replace(/[.,]/g, '');
      }
    }
    
    const parsed = parseFloat(numStr);
    return isNaN(parsed) ? null : Math.round(parsed * 100);
  };

  /**
   * Cents Shoplift added to (or removed from) el's displayed price.
   * `renderedCents` is the unadjusted price the theme server-rendered into
   * el; it seeds the comparison before the first write() tracks the element.
   */
  const adjustment = (el, renderedCents) => {
    if (!el) return 0;
    const shown = parseCents(el.textContent);
    const base = lastWritten.has(el) ? lastWritten.get(el) : renderedCents;
    if (shown == null || base == null) return 0;
    return shown - base;
  };

  /** Write `cents` into el, preserving any active price-test adjustment. */
  const write = (el, cents, format, renderedCents) => {
    const adjusted = cents + adjustment(el, renderedCents);
    lastWritten.set(el, cents);
    el.textContent = format(adjusted);
  };

  return { adjustment, write };
})();
